<template>
	<SimpleModal @close="close">
		<div v-if="showPermissionError" class="no-permissions-error-wrapper">
			<div class="new-team-wrapper no-permissions-error">
				<div
					class="upload-title"
					v-text="$t('apierrors_BUBBLE_ACCESSDENIED')"
				></div>
			</div>
			<div class="center">
				<button
					class="btnActive"
					@click="close"
					v-text="$t('common_button_ok')"
				/>
			</div>
		</div>

		<div
			:class="{
				'profile-card': !currentChat.isdm,
				'profile-card-sm': currentChat.isdm,
				'profile-card-min': showLoader,
				'suppress-inactive-styles': user.inactive,
			}"
			v-if="!showPermissionError && !showMutualGroups"
		>
			<div v-if="isBlocked" class="blocked-text">
				<span v-t="'people_block_blocked'" />
			</div>

			<ProfilePic
				:user="user"
				size="88px"
				class="profile-card-img"
				:class="{ 'blocked-opacity': isBlocked }"
				:enableOnline="!isOwnProfile"
				onlineSize="18px"
			/>

			<div class="profile-name">
				<UserName :user="user" />

				<div class="active-text">
					{{ activeText }}
				</div>

				<transition-group name="scale" tag="div">
					<div v-if="showPronoun" class="user-pronoun-container">
						<div class="user-pronoun">{{ user.pronouns }}</div>
					</div>
					<div v-if="showTitle" class="user-title">{{ user.title }}</div>
				</transition-group>

				<transition name="scale">
					<div
						v-if="responseReturned && !user.inactive"
						class="mutual-groups no-select pointer"
						@click="showMutualGroups = true"
					>
						{{ groupsInCommonText }}
						<img
							src="/images/common/settings-forward-arrow-green.png"
							alt="Common Groups"
						/>
					</div>
				</transition>
			</div>

			<Button
				block
				v-if="user.inactive && dm_id"
				@click="goToDM()"
				:icon="CommentsIcon"
				:iconProps="{ type: 'regular' }"
			>
				{{ $t('people_directmessages_button_title') }}
			</Button>

			<div
				class="profile-buttons"
				:class="{ stacked: canSearch }"
				v-if="!isBlocked && !isOwnProfile && !user.inactive"
			>
				<Button
					@click="createDM"
					:icon="CommentsIcon"
					:iconProps="{ type: 'regular' }"
					:loading="isStartingDM"
				>
					{{ $t('people_directmessage_button_title') }}
				</Button>
				<Button
					@click="search"
					:icon="SearchIcon"
					:iconProps="{ type: 'regular' }"
					v-if="canSearch"
				>
					{{ $t('admin_people_search_action') }}
				</Button>
				<Button
					@click="showBlockConfirmation"
					:icon="UserSlashIcon"
					:iconProps="{ type: 'regular' }"
					:loading="isBlocking"
				>
					{{ $t('people_block_button_title') }}
				</Button>
			</div>

			<div v-if="!currentChat.isdm">
				<div class="profile-buttons" v-if="isOwnProfile">
					<Button pronto block @click="getSettings">
						{{ $t('settings_action_edit') }}
					</Button>
					<Button
						@click="search"
						:icon="SearchIcon"
						:iconProps="{ type: 'regular' }"
						v-if="canSearch"
					>
						{{ $t('admin_people_search_action') }}
					</Button>
				</div>

				<Button danger block v-if="isBlocked" @click="unBlockUser">
					{{ $t('people_unblock_button_title') }}
				</Button>

				<div
					v-if="!user.inactive && !noGroup"
					class="profile-card-line-break"
				></div>

				<div
					v-if="!user.inactive && !noGroup"
					class="profile-card-group-title line-clamp-2"
					v-text="currentChat.displayname"
				/>

				<div class="role-text" v-if="!canChangeUserRole">
					{{ userRoleText }}
				</div>

				<Dropdown
					v-if="canChangeUserRole"
					:options="membershipRoles"
					v-model="userRole"
					global
					class="role-dropdown"
					:loading="isSavingRole"
					:disabled="user.inactive || null"
					@change="changeUserRole"
				/>

				<button
					v-if="!isOwnProfile && !noRole && removeOwnerCheck && !noGroup"
					alt="remove user from group"
					class="link-button remove-from-group-btn"
					@click="removeFromGroup(user.id)"
				>
					<img src="/images/common/remove-icon.png" />
					{{ $t('people_removefromgroup_button_title') }}
				</button>

				<div
					v-if="noRole && !user.inactive && !isBlocked && !noGroup"
					class="removed-state"
				>
					<span v-text="$t('people_user_no_longer_in_group')"></span>
				</div>

				<button
					v-if="isOwnProfile && canRemoveMember"
					alt="remove user from group"
					class="link-button remove-from-group-btn"
					@click="leaveGroup()"
				>
					<span
						><img
							src="/images/common/remove-icon.png"
							style="
								vertical-align: middle;
								margin-bottom: 2px;
								margin-right: 4px;
							"
					/></span>
					<span v-text="$t('people_leavegroup_button_title')"></span>
				</button>
			</div>

			<div v-if="currentChat.isdm">
				<button
					v-if="isOwnProfile"
					block
					class="btn-active-lg profile-card-btn-active edit-user-profile"
					@click="getSettings"
					v-text="$t('settings_action_edit')"
				></button>
			</div>
		</div>

		<MutualGroups
			:partner_id="user.id"
			:profilecard="true"
			:profileMutualgroups="mutualgroups"
			:profileHiddengroups="hiddenGroups"
			@close="close"
			v-if="showMutualGroups"
		/>

		<SpinnerOverlay
			v-if="showSpinner"
			:cTop="cTop"
			:cHeight="cHeight"
			:cLeft="cLeft"
			:cWidth="cWidth"
			:cBackground="cBackground"
			:cBorderRadius="cBorderRadius"
			:sWidth="sWidth"
			:sHeight="sHeight"
			:sMarginTop="sMarginTop"
			:sColor="sColor"
			:cBorder="cBorder"
		/>
	</SimpleModal>
</template>

<script>
import store, {
	getUser,
	setMembership,
	setStat,
	setChat,
	getCurrentChat,
	getDMChats,
	getRecentChats,
} from '@/store'
import { post } from '@/helpers/api'
import {
	SimpleModal,
	UserName,
	ProfilePic,
	Button,
	Dropdown,
	showAlertModal,
	hideAlertModal,
} from '@/components/common'
import { CommentsIcon, SearchIcon, UserSlashIcon } from '@/icons'
import MutualGroups from '@/components/MutualGroups'
import SpinnerOverlay from './SpinnerOverlay'
import moment from 'moment'
import {
	redirectToLastActiveGroup,
	redirectToRecentsOrChat,
} from '@/helperMethods'
import { getTimeAgoShorthand } from '@/helpers/utils'
import { checkAndFetchMembership } from '@/api/membership'
import {
	chatCan,
	REMOVE_MEMBER,
	UPDATE_MEMBERSHIP_ROLE,
} from '@/helpers/permissions'
import { fetchUserInfo } from '@/api/user'
import { orgFlag, USER_TITLE, USER_PRONOUNS, SEARCH } from '@/helpers/flags'
import { markRaw } from 'vue'

export default {
	components: {
		SimpleModal,
		MutualGroups,
		SpinnerOverlay,
		UserName,
		ProfilePic,
		Button,
		Dropdown,
	},
	props: {
		profileData: {
			type: Object,
			required: true,
		},
		noGroup: { type: Boolean, default: null },
	},
	data() {
		const myRole = store.getMembershipRole(store.auth.user.id)
		return {
			store,
			disabled: true,
			hiddenGroups: [],
			isOwnProfile: false,
			showLoader: false,
			options: [],
			item: '',
			isBlocked: false,
			membershipRole: null,
			isSavingRole: false,
			isStartingDM: false,
			mutualgroups: [],
			responseReturned: false,
			showMutualGroups: false,
			showSpinner: false,
			cTop: null,
			cLeft: null,
			cBackground: null,
			cBorderRadius: null,
			cBorder: null,
			sWidth: null,
			sHeight: null,
			sMarginTop: null,
			sColor: null,
			noRole: false,
			showPermissionError: false,
			isBlocking: false,
			userRole: store.getMembershipRole(this.profileData.id),
			myRole,
			iAmMember: myRole === 'member',
			iAmOwner: myRole === 'owner',
			user: this.profileData,
			CommentsIcon: markRaw(CommentsIcon),
			UserSlashIcon: markRaw(UserSlashIcon),
			SearchIcon: markRaw(SearchIcon),
		}
	},

	emits: ['close', 'error'],

	computed: {
		showTitle() {
			return orgFlag(USER_TITLE) && this.user.title
		},

		showPronoun() {
			return orgFlag(USER_PRONOUNS) && this.user.pronouns
		},

		currentChat() {
			return getCurrentChat()
		},

		canSearch() {
			return orgFlag(SEARCH) && !this.store.config.wrapper
		},

		dm_id() {
			const bubble = getDMChats().find((b) => b.dmpartner_id === this.user.id)
			return bubble ? bubble.id : null
		},

		groupsInCommonText() {
			if (!this.responseReturned) return

			const length = this.mutualgroups.length

			return length
				? this.$t('chat_bubbles_common_count.variable', [length], length)
				: this.$t('groupsettings_bubbles_common_count_none')
		},

		activeText() {
			if (this.user.inactive) {
				// if deactivated, show that
				return this.$t('people_presence_inactive')
			}
			const user = getUser(this.user.id)
			if (!user) return null
			if (user.isonline) {
				// if online now, show active
				return this.$t('people_presence_active_now')
			}
			// eslint-disable-next-line vue/no-side-effects-in-computed-properties
			this.store.today = moment().utc().format('YYYY-MM-DD HH:mm:ss')
			const presence = moment(user.lastpresencetime + 'Z')
			const dayCheck = moment(this.store.today).diff(presence, 'days')
			if (dayCheck > 30 || Number.isNaN(dayCheck)) {
				// if more than 30 days ago, show nothing
				return null
			}
			// otherwise, show the timeago timestamp
			const ago = getTimeAgoShorthand(presence)
			return this.$t('people_presence_active_recent', [ago])
		},

		userIsActive() {
			const user = getUser(this.user.id)
			if (!user || !user.lastpresencetime) return false
			return user.isonline
		},

		userRoleText() {
			return this.$t(`model_role_${this.userRole}`)
		},

		membershipRoles() {
			return ['member', 'owner'].map((id) => ({
				id,
				label: this.$t(`model_role_${id}`),
			}))
		},

		removeOwnerCheck() {
			let role = this.store.getMembershipRole(this.store.auth.user.id)
			let user_role = this.store.getMembershipRole(this.user.id)
			if (this.canRemoveMember) {
				if (role === 'member' && user_role === 'owner') {
					return false
				} else {
					return true
				}
			} else {
				return false
			}
		},

		canRemoveMember() {
			return chatCan(this.currentChat, REMOVE_MEMBER)
		},

		isAdmin() {
			return this.user.role === 'admin'
		},

		canChangeUserRole() {
			return (
				!this.isOwnProfile &&
				!this.noGroup &&
				!this.noRole &&
				chatCan(this.currentChat, UPDATE_MEMBERSHIP_ROLE) &&
				!this.isAdmin
			)
		},
	},

	async created() {
		const { user } = await fetchUserInfo(this.user.id)
		this.user = user
		this.getMutualGroups()
	},

	async mounted() {
		this.isSavingRole = true
		await checkAndFetchMembership(this.user.id, store.currentChatId)
		let role = this.store.getMembershipRole(this.user.id)
		if (role) {
			this.membershipRole = role.charAt(0).toUpperCase() + role.slice(1)
			this.userRole = role
		} else {
			this.noRole = true
		}
		this.isSavingRole = false
		if (this.store.auth.user.id === this.user.id) {
			this.isOwnProfile = true
		} else {
			this.isOwnProfile = false
		}
		this.options = getRecentChats().map((b) => ({ id: b.id, label: b.title }))
		let blocked = this.store.auth.user.blocked.find(
			(x) => x.id === this.user.id
		)
		if (blocked) {
			this.isBlocked = true
		}
		// this.$refs.closeButton.focus()
	},

	methods: {
		getMutualGroups() {
			if (this.user.inactive) return
			if (this.user.id === this.store.auth.user.id) return

			post('/api/v1/user.mutualgroups', { user_id: this.user.id })
				.then(({ data }) => {
					data.bubbles.forEach(setChat)
					data.memberships.forEach(setMembership)
					this.mutualgroups = data.bubbles

					this.hiddenGroups = data.memberships
						.filter((m) => m.ishidden)
						.map((m) => m.bubble_id)

					this.responseReturned = true
				})
				.catch((error) => {
					console.error(error)
				})
		},

		getSettings() {
			this.$emit('close')
			this.$router.push('/settings/' + this.user.id + '?s=profile')
		},

		goToDM() {
			const path = `/chat/${this.dm_id}`

			if (this.$route.path !== path) this.$router.push(path)
			EventBus.$emit('closeProfileCard')
		},

		close() {
			if (this.showMutualGroups) {
				this.showMutualGroups = false
				return
			}

			this.$emit('close')
		},

		search() {
			this.$router.push({
				name: 'search',
				query: { users: this.user.id, focus: 1 },
			})
			this.$emit('close')
		},

		removeFromGroup(user_id) {
			this.configureSpinner('.remove-from-group-btn')
			post('/api/v1/bubble.kick', {
				bubble_id: this.store.currentChatId,
				users: [user_id],
			})
				.then(({ data }) => {
					EventBus.$emit('removedUserFromProfileCard', { id: user_id })
					this.showSpinner = false
					this.close()
				})
				.catch((error) => {
					console.error(error)
					this.$emit('error', 'Unable to remove user from group.  Try again.')
					this.close()
				})
		},

		createDM() {
			this.isStartingDM = true

			const dms = getDMChats()
			const existingDM = dms.find((dm) => dm.dmpartner_id === this.user.id)
			if (existingDM) {
				this.isStartingDM = false
				this.close()
				redirectToRecentsOrChat(existingDM.id)
				return
			}

			var postData = {
				user_id: this.user.id,
				organization_id: store.auth.user.organizations[0].id,
			}

			post('/api/v1/dm.create', postData)
				.then(({ data }) => {
					if (data.ok) {
						setChat(data.bubble)
						setStat(data.stats[0])

						// trashy hack to make sure the model is updated before we change routes
						setTimeout(() => {
							this.isStartingDM = false
							this.close()
							this.$router.push('/chat/' + data.bubble.id)
						}, 1000)
					}
				})
				.catch((error) => {
					console.error(error)
					this.isStartingDM = false
					if (error.response?.data?.error === 'PERMISSION_DENIED') {
						this.showPermissionError = true
						return
					}
					this.close()
				})
		},

		showBlockConfirmation() {
			showAlertModal({
				title: this.$t('people_block_confirm_title', {
					UserName: this.user.fullname,
				}),
				description: this.$t('people_block_confirm_message'),
				confirmText: this.$t('people_block_button_title'),
				confirmProps: { danger: true },
				hideCancel: false,
				icon: UserSlashIcon,
				onConfirm: () => {
					hideAlertModal()
					this.blockUser()
				},
			})
		},

		async blockUser() {
			try {
				this.isBlocking = true
				const { data } = await post('/api/v1/user.block', {
					user_id: this.user.id,
				})
				this.isBlocked = true
				this.store.auth.user.blocked = data.user.blocked
			} catch (error) {
				console.error(error)
				this.$alert.error(error)
			} finally {
				this.isBlocking = false
			}
		},

		unBlockUser() {
			var postData = {
				user_id: this.user.id,
			}
			post('/api/v1/user.unblock', postData)
				.then(({ data }) => {
					this.showSpinner = false
					this.isBlocked = false
					this.store.auth.user.blocked = data.user.blocked
				})
				.catch((error) => {
					console.error(error)
					this.$emit('error', 'Unable to unblock user.  Try again.')
					this.close()
				})
		},

		async changeUserRole(role) {
			if (store.getMembershipRole(this.user.id) === role) return
			this.isSavingRole = true
			try {
				const { data } = await post('/api/v1/membership.setuserrole', {
					bubble_id: store.currentChatId,
					user_id: this.user.id,
					role,
				})

				setMembership(data.membership)
				EventBus.$emit('userRoleUpdated', {
					user_id: data.membership.user_id,
					role: data.membership.role,
				})
			} catch (error) {
				console.error(error)
				if (error.response?.data?.error === 'LAST_OWNER_ERROR') {
					EventBus.$emit('showLastOwnerPopup')
				}
				this.close()
			}
			this.isSavingRole = false
		},

		configureSpinner(element_id) {
			let element = document.querySelector(element_id)
			if (element) {
				let position = element.getBoundingClientRect()
				let style = getComputedStyle(element, null)
				this.cTop = position.top + 'px'
				this.cLeft = position.left + 'px'
				this.cHeight = style.height
				this.cWidth = style.width
				this.cBackground = style.backgroundColor
				this.cBorderRadius = style.borderTopLeftRadius
				this.sHeight = '30px'
				this.sWidth = '30px'
				this.sMarginTop = (element.clientHeight - 30) / 2 + 'px'
				this.showSpinner = true
				this.sColor = style.color
				this.cBorder =
					style.borderTopColor +
					' ' +
					style.borderTopStyle +
					' ' +
					style.borderTopWidth
			}
		},

		leaveGroup() {
			this.configureSpinner('.remove-from-group-btn')
			post('/api/v1/bubble.leave', { bubble_id: this.store.currentChatId })
				.then(({ data }) => {
					this.close()
					//window.history.back();
					redirectToLastActiveGroup()
				})
				.catch((error) => {
					console.error(error)
					if (error.response?.data?.error === 'LAST_OWNER_ERROR') {
						this.showSpinner = false
						EventBus.$emit('showLastOwnerPopup')
					}
					this.close()
					this.$alert.error()
				})
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

#btnMessage,
#btnBlock {
	display: inline-block;
	min-width: 110px;
	border-radius: 3px;
}

#btnInactive {
	min-width: 180px;
	color: #363a3b;
}

.unblock-user-button,
.edit-user-profile {
	min-width: 230px;
}

.remove-from-group-btn {
	@include flex-center(8px);
	color: var(--neutral-800);
	font-family: Avenir-Roman;
	font-size: 14px;
	margin-top: 16px;
	background: transparent;
	width: 100%;
}

.profile-card,
.profile-card-sm {
	width: 330px;
	box-shadow: none;
	position: relative;
	padding: 24px;
	.user-pronoun-container {
		display: flex;
		justify-content: center;
		margin: 5px 0 5px 0;
		.user-pronoun {
			background: var(--neutral-600);
			border-radius: 6px;
			font-family: Avenir-Heavy;
			font-size: 12px;
			color: var(--neutral-0);
			padding: 5px 8px;
			max-width: max-content;
			text-align: center;
		}
	}
	.user-title {
		font-family: Avenir-Heavy;
		font-size: 14px;
		color: var(--neutral-500);
		margin: 5px 0 5px 0;
	}
}

.profile-card-img {
	margin: 0 auto 8px auto;
	:deep(.online-indicator) {
		right: 2px;
		bottom: 2px;
	}
}

.profile-card-line-break {
	border-bottom: 1px solid var(--neutral-40040);
	width: 100%;
	margin: 24px 0;
}

.profile-card-btn-active {
	height: 45px;
	font-family: Avenir-Heavy;
	font-size: 14px;
}

.profile-card-group-title {
	text-align: center;
	font-family: Avenir-Medium;
	font-size: 18px;
	color: var(--neutral-800);
	margin-bottom: 9px;
	padding-left: 10px;
	padding-right: 10px;
}

.select-margin {
	margin-top: 51px;
}

.no-select {
	user-select: none;
}

.profile-card-selected-role {
	width: 230px;
	border: 2px solid #b7b9bd;
	height: 26px;
	border-radius: 5px;
	font-family: Avenir-Heavy;
	font-size: 14px;
	padding-top: 2px;
	color: #b7b9bd;
	margin-left: 30px;
	position: relative;
	background-color: #ffffff;
	cursor: pointer;
}

.profile-card-selected-role input {
	position: absolute;
	float: right;
	vertical-align: middle;
	right: 10px;
	margin-top: 6.5px;
}

.profile-card-selected-role-owner {
	border: 2px solid var(--teal-300);
	color: var(--teal-300);
}

.profile-card-role-selection {
	width: 230px;
	border: 2px solid #b7b9bd;
	color: #b7b9bd;
	height: 54px;
	z-index: 1;
	position: absolute;
	background-color: white;
	border-radius: 5px;
	margin-left: 30px;
	-webkit-box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.25);
	-moz-box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.25);
	box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.25);
	cursor: pointer;
}

.profile-card-role-selection div {
	height: 26px;
	padding-top: 4px;
	font-family: Avenir-Heavy;
	font-size: 14px;
	cursor: pointer;
}

.profile-card-role-selection div:hover {
	background-color: whitesmoke;
	color: var(--teal-300);
}

.profile-card-role-selection div:hover .profile-card-role-selection-input {
	background-color: whitesmoke;
}

.profile-card-role-selection div:nth-child(1) {
	position: relative;
	border-top-right-radius: 5px;
	border-top-left-radius: 5px;
}

.profile-card-role-selection div:nth-child(1) input {
	margin-top: 0;
	position: absolute;
	right: 10px;
	top: 9px;
}

.profile-card-role-selection div:nth-child(2) {
	border-bottom-right-radius: 5px;
	border-bottom-left-radius: 5px;
}

.blocked-text {
	position: absolute;
	top: 55px;
	left: 0;
	width: 100%;
	> span {
		color: $error-red;
		font-family: Avenir-Heavy;
		font-size: 25px;
	}
}

.blocked-opacity {
	opacity: 0.15;
}

#imageClose {
	outline: none;
	top: 16px;
	right: 20px;
}

.profile-name {
	font-family: Avenir-Heavy;
	font-size: 22px;
	color: var(--neutral-800);
	margin-bottom: 21px;
	padding-left: 10px;
	padding-right: 10px;
	word-break: break-word;
}

.profile-buttons {
	width: 230px;
	margin: 0 auto;
	@include flex-center;

	&.stacked {
		width: auto;
		.pronto-btn {
			height: 64px;
			width: 78px !important;
			padding: 0 8px;
			flex-direction: column;
			width: auto;
			:deep(.pronto-icon) {
				font-size: 18px;
			}
		}
	}

	.pronto-btn {
		width: 113px;
		@include flex-center(8px);
		&.default {
			border-color: var(--neutral-200);
		}
	}
}

.role-dropdown {
	:deep(.dropdown-container) {
		.dropdown-selected {
			height: 30px;
			background: var(--neutral-200);
		}
	}
}

.role-text {
	@include flex-center;
	height: 30px;
	border: 1px solid var(--neutral-40040);
	padding: 0 12px;
	border-radius: 6px;
}

.active-text {
	font-family: Avenir-Medium;
	font-size: 14px;
	color: rgb(183, 185, 189);
	height: 20px;
}

:deep(.spinner-div) {
	position: fixed;
}

.removed-state {
	font-family: 'Avenir-Heavy';
	font-size: 16px;
	border: 2px solid #9498a3;
	border-radius: 1px;
	color: #9498a3;
	margin-left: 35px;
	margin-right: 35px;
}

.mutual-groups {
	font-family: Avenir-Heavy;
	font-size: 14px;
	color: var(--teal-300);
	margin-bottom: 2px;
	> img {
		height: 9px;
	}
}

.mutual-groups-wrapper {
	width: 490px;
	height: unset;
	border-radius: 15px;
	background-color: #ffffff;
	box-shadow: 0 8px 24px 0 #595959;
	padding-bottom: 20px;
}

:deep(.mutual-groups-list) {
	max-height: 370px;
	overflow-y: auto;
	overflow-x: hidden;
}

.category-title {
	font-family: Avenir;
	color: #4f5053;
	font-size: 10px;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
	text-transform: uppercase;
	width: 100%;
	padding: 0 20px;
	margin-bottom: 5px;
}

.mutual-group-title {
	margin: 0 auto;
	font-family: Avenir-Heavy;
	font-size: 15px;
	color: #4f5053;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
	position: relative;
	padding: 0 20px;
}

.mutual-group {
	cursor: pointer;
	padding: 10px 0;
	margin: 0 20px;
	border-radius: 12px;
}

.mutual-group:hover {
	background-color: #fafafa;
}

.groups-spacer {
	margin-bottom: 41px;
}

.suppress-inactive-styles {
	min-height: unset !important;
	box-shadow: unset !important;
}

.no-permissions-error-wrapper {
	max-width: 400px;
	padding: 20px;
}

.edit-user-profile {
	color: var(--teal-300) !important;
}

.scale-enter-active {
	transition: transform 0.4s ease, opacity 0.4s ease;
}

.scale-enter-from {
	transform: scale(0.8);
	opacity: 0;
}

.scale-enter-to {
	transform: scale(1);
	opacity: 1;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.profile-card {
		background: var(--neutral-300);
		color: var(--neutral-800);
		.dropdown-selected {
			.dropdown-selected-loading {
				background: var(--neutral-200);
			}
		}
	}
	.dropdown-parent {
		.dropdown-options-container {
			.dropdown-option {
				height: 30px;
				padding: 0 12px;
				@include flex;
			}
		}
	}
}
</style>
